import {
  useId,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import {
  coverageAnnotationFields,
  formatTimeOfDay,
  type CoverageAnnotation,
  type ResolvedCoverageAnnotation,
} from '@vendorops/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { TimelineCoverageBand } from '@/lib/timeline-geometry';
import type { DragKind } from '@/hooks/use-timeline-drag';
import { EDGE_PX, EdgeHandles, ReadoutPlate, type TimelineReadout } from '@/components/TimelineGrab';
import {
  DETAIL_FIELD,
  DetailRow,
  FieldButton,
  PANEL_TIME,
  TimeField,
} from '@/components/TimelineFields';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { dayText } from '@/components/TimelineItem';

/**
 * The vendors' booked hours, as an annotation track of its own beside the day.
 *
 * Split out of `TimelineAxis.tsx` rather than added to it, and the line is a
 * real one: the axis draws the wedding's own day — hours, dividers, blocks,
 * markers — and this file draws a second thing that runs parallel to it and
 * shares only the vertical scale. The axis was already ~1000 lines of that
 * first job. What stays in the axis is the only number the two must agree
 * about: how much width the track takes, which is `TRACK_REM` below and is
 * where the day's own right edge stops.
 *
 * **Two rejected designs are why this is a track and not a rail**, and the
 * reason is one reason twice over.
 *
 * The first drew coverage as translucent bands across the lane area *behind*
 * the blocks, created from a form in the panel's header. The fill could not
 * carry its own meaning — clay at 8% measures 1.09:1 on the white card, below
 * perception — so a left-hand inset was the only thing telling two windows
 * apart, and that ran out of card at four vendors.
 *
 * The second drew a thin rail between the hour gutter and the lanes: a
 * six-pixel column of capsules, with the labels floating out over the day
 * because six pixels could not hold a word. It was sent back as invisible —
 * *"it's really not obvious that the rail is a place where you can add or edit
 * these annotations… It feels like you have to hover over it and know what the
 * coverage annotation is."* Every affordance it grew was a compensation for
 * that: a dashed guide so the empty column read as a surface, a wash under the
 * pointer so it acknowledged one, a caption above the scroller because the
 * column had nowhere to put its own name, a pencil on each floating label
 * because a chip over the day read as a printed fact. Four mechanisms, all
 * answering *is anything meant to happen here?*, none of them answering it
 * before the planner hovered.
 *
 * The diagnosis was that coverage was being squeezed into the furniture of the
 * day. So it is not in that furniture any more: a separate column to the right
 * of the lanes, on the same timing scale, with its own filled ground, its own
 * sticky heading and — while it is empty — a sentence saying what to do. A
 * column wide enough to hold its own labels needs none of those four
 * compensations, and all four are gone.
 */

/** A window as the track is handed it — resolved, with the server's `hours`. */
export type TrackCoverage = ResolvedCoverageAnnotation<CoverageAnnotation>;

/**
 * What a sweep posts once it has been given a tag.
 *
 * The offset grammar, and that is the whole point of the shape. A planner
 * typing "11:00 AM" has not said which day they mean, and the route's
 * `offsetForClockTime` has a documented twelve-hour window for guessing; a
 * sweep has named two pixels, and a pixel is already an offset. Handing it to
 * the guess loses the only unambiguous thing about it — against a 5:30 PM
 * wedding it turns midnight at the top of the axis into midnight at the foot of
 * it. `TimelineItemPlacement` in `TimelineItem.tsx` records the same reasoning
 * for a click that creates an event, and every word of it applies here.
 *
 * **Both tags are always named, one of them null.** Not an accident of the
 * shape: `coverageTagsNamedTogether` in `shared/schemas/coverage.ts` refuses any
 * request that names one tag alone, because whether `{ category: null }` leaves
 * a row with no tag at all depends on what is already stored.
 */
export interface CoveragePlacement extends CoverageTags {
  offsetMinutes: number;
  durationMinutes: number;
}

/** Which tag a window carries, with the other one stated as cleared. */
export interface CoverageTags {
  category: string | null;
  label: string | null;
}

/**
 * What one PATCH's worth of *retime* on an existing window carries. Absent
 * means "leave it alone"; no tag appears here at all, by design.
 *
 * Not `TimelineRetime` reused, and the difference is one `null`. A timeline
 * item's `durationMinutes` is nullable because there is always something
 * downstream to borrow a length from — the next item, or the end of the night
 * — so null means "the end stays where it is". A coverage window has no such
 * chain and always states its own end, so `null` is not a length it can have
 * and the route refuses one. Reusing the item's type would make that refused
 * payload expressible here.
 */
export interface CoverageRetime {
  offsetMinutes?: number;
  durationMinutes?: number;
}

/**
 * The shortest window the API will accept, read off the schema that enforces it
 * rather than retyped.
 *
 * `coverageAnnotationFields.shape.durationMinutes` is `.min(5)` — one snap
 * step, the smallest increment the timeline's own drag deals in. A sweep
 * carrying its own `5` is a sweep that can post what the API refuses, or refuse
 * what the API accepts, the moment that number moves.
 *
 * `minValue` is `number | null` on a ZodNumber, null only if the minimum were
 * removed entirely; a `0` floor then leaves the server as the only thing with
 * an opinion, which is the right fallback — it answers with its own sentence.
 */
export const MIN_WINDOW_MINUTES = coverageAnnotationFields.shape.durationMinutes.minValue ?? 0;

/**
 * How long a window a press that swept nothing makes.
 *
 * An hour, and it is not the same kind of number as `MIN_WINDOW_MINUTES`. That
 * one is the schema's floor on a span the planner *drew*; this is what a click
 * means. A click on the empty day beside the track makes an item at that
 * minute; a click on the empty track makes a window at that hour, which is the
 * parity the review asked for.
 *
 * An hour rather than the five-minute floor because a five-minute coverage
 * window is not a thing anybody books — it is ten pixels of capsule the planner
 * then has to find an edge of and drag. An hour is 120px at this scale: a shape
 * with a body to grab, two ends to re-length and room for both its lines of
 * text, and short enough that the planner is plainly expected to adjust it. It
 */
export const DEFAULT_WINDOW_MINUTES = 60;

/**
 * How much width the track takes from the card — fixed, and never zero.
 *
 * **The one number the day and the track must agree about**, and the only
 * horizontal number either of them is allowed to name. The axis imports it to
 * bound everything that used to run to `right-0` — the block list, the markers,
 * the hour rules, the day dividers and the new-item draft — because a second
 * hard-coded inset anywhere in that list is how the ruler comes to disagree
 * with the lanes about where the day ends.
 *
 * Eleven rem, and two separate decisions are recorded in that being a constant
 * rather than a function of the content.
 *
 * *Why wide.* The rail this replaces sized itself from the lane count — one
 * capsule's 0.375rem at the least — and six pixels of column cannot hold a
 * word, so the labels floated out over the day and the name of the track
 * floated above the scroller. Everything that went wrong with it followed from
 * that: *"it's really not obvious that the rail is a place where you can add or
 * edit these annotations."* A track the day gives no room to is a track nobody
 * finds. Eleven rem holds `Photographer` and `11:00 AM – 8:00 PM` on two lines
 * inside the capsule, the word `Coverage` as a heading, the sentence that
 * invites the first gesture, and the tag editor — none of which has to be drawn
 * over the day any more.
 *
 * *Why fixed.* A width that changed with content shoved the whole day sideways
 * mid-gesture: a provisional capsule in a column of its own would have widened
 * the rail at the instant the planner pressed, moving the ground under the very
 * gesture being measured against it, which the old design had to work around by
 * drawing every provisional shape in lane 0 on top of whatever was already
 * there. A fixed column kills that class of bug outright — nothing the planner
 * does to the track can move the day.
 *
 * The page is `max-w-5xl`, so the card holds about 58rem of content: the gutter
 * and its gap take 3.75rem, the track takes this, and the lanes keep about
 * 43rem — more than the blocks had before coverage existed at a wedding with
 * four overlapping windows, because that case used to charge the day for a
 * fourth capsule column.
 */
export const TRACK_REM = 11;

/**
 * The track's filled column, drawn by the axis *before* its hour rules.
 *
 * Split from `CoverageTrack` so the ruler can run through the column rather
 * than stop at it. The axis sets no z-index anywhere — document order is the
 * layer order, which is what keeps its layering checkable by reading downward —
 * so ground, then rules, then capsules can only be had by letting the ground be
 * a separate element earlier in the markup.
 *
 * `--color-surface-container-high` is **1.27:1** against the white card: the
 * step that makes this read as a separate area rather than a stripe. The
 * hairlines are **1.15:1** on that ground, the same weight the hour rules carry
 * on the card, which is where a structural hairline belongs. They are not what
 * separates the two areas — the ground's own step is — they are what keeps that
 * step from reading as a soft gradient at either seam.
 *
 * **Both edges, not just the left.** With only the left one the column had no
 * closing edge, so it bled into the hour gutter beyond it and that gutter's
 * labels read as sitting *inside* the column rather than outside it, which the
 * left-hand gutter never does. A ruled edge on each side is what makes both
 * gutters read the same way: outside, naming the column rather than belonging
 * to it.
 */
export function CoverageGround({ rightRem }: { rightRem: number }) {
  return (
    <div
      data-coverage-ground
      aria-hidden="true"
      className={`pointer-events-none absolute inset-y-0 border-x border-[var(--color-border)] ${GROUND}`}
      style={{ width: `${TRACK_REM}rem`, right: `${rightRem}rem` }}
    />
  );
}

/**
 * The track's own inner gutter, so a capsule never touches the hairline border
 * or the card beyond it.
 *
 * Shared by the heading, the empty-track prompt, the capsules and the editor:
 * one inset means the heading's word starts on the same pixel the capsules
 * under it do, which is what makes the column read as one object with one name.
 */
const TRACK_PAD = 'left-2 right-2';

/**
 * The gap between two capsules in neighbouring lanes.
 *
 * Taken out of each capsule's own width rather than added between them, which
 * is `TimelineBlock`'s idiom one step simplified (`mr-[3px]` on a box inside
 * the positioned one, there). A `calc()` instead of a nested box because the
 * capsule is the drag handle, the click target and the focusable element all at
 * once: a wrapper around it would be the thing a test and a pointer landed on,
 * and an event on a parent does not reach a child, so every gesture would have
 * to know which of the two shapes it was aimed at.
 */
const LANE_GAP_PX = 3;

/**
 * The track's ground, and the single most important thing in this change.
 *
 * `--color-surface-container-high` is #e9e4dc — **1.27:1** against the white
 * card. That is far below the 3:1 a graphic carrying information has to clear,
 * and that is correct: the fill carries no fact. What it does is make the
 * column read as a separate *area* rather than a stripe on the day, which is
 * the thing the two rejected designs never managed. A band behind the blocks
 * and a six-pixel rail between the gutter and the lanes were both inside the
 * day's own furniture, and no amount of dashes, washes or captions could say
 * otherwise. A filled column says it before anything is drawn in it.
 *
 * Written `bg-[var(--color-surface-container-high)]` rather than
 * `bg-surface-container-high`, and that is not a style preference: this app's
 * custom tokens live in `:root` in `app.css` and not in `@theme`, so Tailwind
 * mints no colour name for them and the short form compiles to *nothing at
 * all*. Silently nothing — an unfilled track is the invisible rail again, with
 * every contrast figure in this file quietly untrue. Same for
 * `--color-coverage` below.
 *
 * A constant because two things need the identical fill: the column itself and
 * the sticky heading, which must be opaque or capsules scroll through the word
 * naming them. Those two drifting half a step apart reads as a seam.
 */
const GROUND = 'bg-[var(--color-surface-container-high)]';

/**
 * Secondary text on the ground, at **5.06:1**.
 *
 * Not `text-muted-foreground`, which is what every other secondary line on this
 * panel uses. `TimelinePanel`'s `Empty` records the measurement: muted grey
 * reaches 4.55:1 on the white card and only **3.59:1** on
 * `--color-surface-container-high`, where it fails the 4.5:1 floor for body
 * text. The track is a filled container, so it inherits that finding outright.
 * `/70` comes to 4.43 and misses; `/75` is the lightest step that clears.
 *
 * It matters most for the prompt, which is the one sentence a planner has to
 * read to discover the feature at all.
 */
const ON_GROUND = 'text-foreground/75';

/**
 * How tall a capsule has to be before it gets resize edges, and how tall they
 * are.
 *
 * Below three edges' worth there is no body left between them, and a capsule
 * with no body cannot be moved — only re-lengthed from whichever edge the
 * pointer happened to land on. `TimelineBlock` gates its own edges the same way
 * for the same reason.
 */

/** Trimmed, so a third of the track is `33.3333%` and not seventeen digits. */
function percent(value: number): string {
  return `${Number(value.toFixed(4))}%`;
}

/**
 * Where a capsule in this lane sits across the track, as a fraction of it.
 *
 * Percentages of the track's inner width rather than rem arithmetic, because
 * the width is fixed and the lane count is not: `assignLanes` gives every
 * capsule in an overlap group the same `laneCount`, so dividing the column is
 * the whole of what two overlapping windows need. This is the same division
 * `place()` does for overlapping blocks in `TimelineAxis.tsx`, and deliberately
 * so — a window beside a window should look like an event beside an event.
 */
function laneBox(lane: number, laneCount: number): { left: string; width: string } {
  return {
    left: percent((lane / laneCount) * 100),
    width: `calc(${percent(100 / laneCount)} - ${LANE_GAP_PX}px)`,
  };
}

/**
 * The coverage track: a column of its own, to the right of the day.
 *
 * Four layers, in document order, and the order is the whole statement —
 * nothing on this axis sets a z-index, so reading downward is how the layering
 * is checked:
 *
 * 1. the ground and its hairline border, which is the column;
 * 2. the span being swept, and the one a draft is waiting to name — so a
 *    stored window always reads over a shape that is not one yet;
 * 3. the capsules;
 * 4. the tag editor, which has to be reachable over everything under it.
 *
 * The provisional shapes span the full width rather than taking a lane of their
 * own, and the fixed width is what makes that safe: on the old rail a
 * provisional column widened the rail and shoved the day sideways mid-press, so
 * every one of them had to be crammed into lane 0 on top of whatever was
 * already there. Here they are simply underneath.
 */
export default function CoverageTrack({
  bands,
  sweeping,
  draft,
  onSweepPointerDown,
  onSweepPointerMove,
  onSweepPointerUp,
  onSweepPointerCancel,
  dragging,
  onCapsulePointerDown,
  onCapsulePointerMove,
  onCapsulePointerUp,
  onCapsulePointerCancel,
  onNudge,
  categories,
  editing,
  rightRem,
  onEdit,
  onRetag,
  onRetimeStart,
  onRetimeEnd,
  onRemove,
  onCancel,
  onPlace,
  onCancelDraft,
}: {
  bands: readonly TimelineCoverageBand<TrackCoverage>[];
  /** The span under the pointer right now, drawn provisionally. */
  sweeping: { top: number; height: number } | null;
  /**
   * The span waiting for a tag — drawn, and draggable while it waits.
   *
   * Everything about it in one object, the same arrangement `dragging` uses:
   * where it is, what a gesture on it is saying, and the handlers that gesture
   * runs through. A separate `draftTop` used to position its tag control, and
   * two props naming the same span is two props that can disagree about where
   * it is once it can move.
   */
  draft: {
    top: number;
    height: number;
    /** What a resize is saying, or null when nothing is held. */
    readout: TimelineReadout | null;
    /** Absent where no placement is wired, which is most of the test suites. */
    grab?: {
      onPointerDown: (kind: DragKind, event: ReactPointerEvent<HTMLElement>) => void;
      onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
      onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
      onPointerCancel: () => void;
    };
  } | null;
  onSweepPointerDown?: (event: ReactPointerEvent<HTMLElement>) => void;
  onSweepPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onSweepPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  onSweepPointerCancel: () => void;
  /** Which capsule is mid-drag, and where it is drawn while it is. */
  /**
   * The window under the pointer: where it now is, and what that says.
   *
   * One object rather than a placement and a readout side by side, because the
   * two are statements about the same capsule and two props could be made to
   * disagree about which one.
   */
  dragging: { id: string; top: number; height: number; readout: TimelineReadout } | null;
  onCapsulePointerDown?: (
    annotation: TrackCoverage,
    kind: DragKind,
    event: ReactPointerEvent<HTMLElement>,
  ) => void;
  onCapsulePointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onCapsulePointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  onCapsulePointerCancel: () => void;
  onNudge?: (annotation: TrackCoverage, steps: number, fine: boolean) => void;
  /**
   * The planner's own category names, for the tag control to offer.
   *
   * A prop, not a query of this file's own. The axis is handed every fact it
   * draws and fetches nothing — that is what lets a thousand lines of axis
   * tests run in a bare `render` with no query client and no stubbed API, and a
   * `useQuery` down here would have cost all of them that. `TimelinePanel` owns
   * the read, on the same `['categories']` key `AddVendorDialog` uses.
   */
  categories: readonly string[];
  /** Which window's tag editor is open, if any. */
  editing: string | null;
  /** How far the hour gutter on the right holds the track off the edge. */
  rightRem: string;
  onEdit?: (annotation: TrackCoverage) => void;
  onRetag?: (annotation: TrackCoverage, tags: CoverageTags) => void;
  /**
   * A typed start, and a typed end, both as the clock times they were typed as.
   *
   * Clock in, clock out: turning one into an offset needs the wedding's own
   * start time, and this file has never been told it. The axis has it, owns the
   * clamp that keeps an offset inside what the schema stores, and already has
   * one path that narrows a retime — so it does the arithmetic and this stays
   * about drawing. See `coverageDurationForEnd` for why an end needs its own
   * rule rather than the one a start uses.
   */
  onRetimeStart?: (annotation: TrackCoverage, clockTime: string) => void;
  onRetimeEnd?: (annotation: TrackCoverage, clockTime: string) => void;
  onRemove?: (annotation: TrackCoverage) => void;
  onCancel: () => void;
  onPlace?: (tags: CoverageTags) => void;
  onCancelDraft: () => void;
}) {
  /**
   * Whether a press on the ground would make anything.
   *
   * The track itself is drawn either way, which is the reversal this redesign
   * is: the column, its border, its ground and its heading are there on every
   * wedding, before any window exists and even on an axis mounted with no way
   * to place one — because the day's right edge is bounded at `TRACK_REM`
   * regardless, and a bounded day beside no column is a white gap nobody can
   * account for. What *is* gated is the prompt: a sentence inviting a gesture
   * nobody wired is a promise this file cannot keep. The panel always wires the
   * handler; the drag, edit and detail suites mount the axis without it.
   */
  const drawable = onSweepPointerDown !== undefined;

  /**
   * How many columns to divide the track into.
   *
   * The widest overlap group's, because `assignLanes` gives every capsule in a
   * group the same `laneCount` — a wedding with a photographer all day and a
   * florist in the afternoon needs two columns however many back-to-back
   * windows sit under them. `Math.max(1, …)` covers the empty track, where
   * there is nothing to divide.
   */
  const laneCount = Math.max(1, ...bands.map((band) => band.laneCount));


  return (
    <div
      data-coverage-track
      /* The full height of the canvas, so the track keeps the axis's scale down
         the whole calendar day. The width is `TRACK_REM` and nothing else: see
         that constant for why it is not derived from the lane count and not
         zero when empty. `rightRem` is the hour gutter standing to its right.

         **Paints nothing.** The ground is `CoverageGround`, drawn by the axis
         before the hour rules, so that the ruler runs *through* this column
         instead of stopping at it — a capsule is read against the clock, and a
         clock that stops at the column's edge makes a planner track back across
         the whole day to read one. Document order is the layer order on this
         axis and nothing here sets a z-index, so the only way to get ground,
         then rules, then capsules is to let a different component own the
         ground. What is left here is geometry and the pointer surface.

         `touch-none` is not optional: without it the browser claims the gesture
         as a page scroll and a window can never be drawn on a phone. */
      className="absolute inset-y-0 touch-none"
      style={{ width: `${TRACK_REM}rem`, right: rightRem }}
      onPointerDown={onSweepPointerDown}
      onPointerMove={onSweepPointerMove}
      onPointerUp={onSweepPointerUp}
      onPointerCancel={onSweepPointerCancel}
    >
      {/* The track's name, and — while there is nothing in it — what to do.
          Sticky at the top of the scroller, which is the one thing the old
          caption above the scroller could not be and the one thing it needed to
          be: the canvas is a whole calendar date tall under a 70vh cap, and the
          axis opens scrolled to event start, so a heading fixed to the top of
          the *canvas* names the column from midnight of a day the planner is
          not looking at. Sticky, it is on screen at whatever hour they have
          scrolled to — which is the hour they are about to draw at.

          Opaque, in the track's own ground, so capsules passing under it do not
          show through the word naming them. The cost is real and accepted: a
          window opening in the first ~28px of the canvas is partly behind the
          heading until the planner scrolls, which is the same trade every
          sticky calendar header makes.

          `pointer-events-none`, which is what makes the prompt honest. The
          sentence says a click makes a window, so a click *on the sentence* has
          to make one — and it does, because the press falls through to the
          track behind it and is read as the offset at that pixel, which under a
          sticky heading is the top of the current view. A live heading would be
          a line of text telling the planner to click that swallowed the
          click. */}
      <div
        data-coverage-heading
        className={`pointer-events-none sticky top-0 px-2 pt-1.5 pb-2 ${GROUND}`}
      >
        <div className={`label-caps text-[0.625rem] ${ON_GROUND}`}>Coverage</div>

        {/* The empty track announcing itself, and the whole reason this
            redesign exists.

            Two designs were rejected as invisible features. Both were findable
            only by hovering a strip and already knowing what a coverage
            annotation was; both tried to say so with graphics — dashes, a wash,
            a pencil — because neither had room for a sentence. This has room
            for a sentence, so it uses one. It is on screen with no hover, no
            click and no prior knowledge, which is the acceptance test the other
            two failed.

            Plain text at 5.06:1 on the ground (see `ON_GROUND`) rather than a
            tooltip, a placeholder or anything else that waits to be summoned.
            Under the heading and inside the same sticky block deliberately: the
            axis opens scrolled to event start, so a prompt pinned to the top of
            the canvas would be a screen and a half above the fold on open —
            absent exactly when the planner first looks, which is the failure
            being fixed.

            It goes once there is a window, because by then the capsules say it
            better: a planner who has drawn one knows the column is drawable. */}
        {drawable && bands.length === 0 && (
          <p data-coverage-prompt className={`mt-1 text-xs leading-snug ${ON_GROUND}`}>
            Click or drag to set a vendor&rsquo;s hours
          </p>
        )}
      </div>

      {/* Everything placed by the clock, in one inset box so that the capsules,
          the provisional shapes and the editor all measure their lanes from the
          same edges the heading's word starts on.

          `pointer-events-none` on the box and back on for each shape it holds,
          which is the `<ol>`'s own idiom in the axis and for the same reason:
          the box spans the entire track, and left live it would be the thing
          every press on the empty ground landed on rather than the track that
          reads a press as a sweep. */}
      <div
        className={`pointer-events-none absolute inset-y-0 ${TRACK_PAD}`}
        data-coverage-lanes={laneCount}
      >
        {/* And the window a click would make, under the pointer.

            Not while either provisional shape below is on screen, which is the
            whole of when it must not appear. Mid-sweep the swept span is what
            speaks — it is the length being measured, and a second translucent
            shape arguing about a different one is the one thing the planner
            must not have to read. Between a sweep and its tag the draft is that
            shape. */}
        {/* The span under the pointer, and the span waiting for its tag — one
            shape, two moments of the same gesture. Both full width, both under
            the capsules: see the component comment for why the fixed width is
            what lets them be. */}
        {sweeping && (
          <ProvisionalCapsule top={sweeping.top} height={sweeping.height} testId="coverage-sweep" />
        )}
        {draft && (
          <DraftCapsule
            top={draft.top}
            height={draft.height}
            readout={draft.readout}
            grab={draft.grab}
            categories={categories}
            onPlace={onPlace}
            onCancel={onCancelDraft}
          />
        )}

        {bands.map((band) => (
          <CoverageCapsule
            key={band.annotation.id}
            band={band}
            laneCount={laneCount}
            drawn={dragging?.id === band.annotation.id ? dragging : null}
            onPointerDown={onCapsulePointerDown}
            onPointerMove={onCapsulePointerMove}
            onPointerUp={onCapsulePointerUp}
            onPointerCancel={onCapsulePointerCancel}
            onOpen={onEdit}
            onNudge={onNudge}
            panel={{
              categories,
              onPickTag: (tags) => onRetag?.(band.annotation, tags),
              onStart: onRetimeStart
                ? (clock) => onRetimeStart(band.annotation, clock)
                : undefined,
              onEnd: onRetimeEnd ? (clock) => onRetimeEnd(band.annotation, clock) : undefined,
              onRemove: onRemove ? () => onRemove(band.annotation) : undefined,
              onCancel,
            }}
            selected={band.annotation.id === editing}
          />
        ))}


      </div>
    </div>
  );
}

/**
 * One vendor's booked hours: a block in the track, naming itself.
 *
 * **A block now, not a bar**, and that is the change the whole redesign turns
 * on. The rail's capsule was a 0.375rem sliver of colour whose label floated
 * out over the day on a chip, because six pixels cannot hold a word; this is
 * the same kind of object as a `TimelineBlock` and borrows its idioms rather
 * than inventing new ones — a filled box at the geometry's `top` and `height`,
 * its own text inside it, `truncate` with the full string in `title` when the
 * box is too narrow for it, rounded like a block rather than a pill.
 *
 * Solid `--color-coverage` with white text: **4.94:1**, so the words are
 * readable rather than decorative, which they have to be — they are now the
 * whole of what says *who*. The fill measures **3.90:1** against the track's
 * own ground, over the 3:1 a graphic carrying meaning must clear, so the shape
 * reads as a shape even before the text in it is read.
 *
 * No coloured left border, which is the obvious thing to reach for and a banned
 * default in this project's design floor — a thick coloured side rule is the
 * generic "category stripe" that makes every surface in an app look like the
 * same dashboard.
 *
 * And a category-tagged window is drawn identically to a free-text one. A
 * category feeds `{{coverage_hours}}` and a free-text label does not, but that
 * difference belongs where the variable is merged, not as a second colour
 * language a planner has to learn to read the timeline.
 *
 * One control, two gestures, which is what the floating label's deletion buys:
 * drag the body to move it, drag an end to re-length it, click it to retag or
 * remove it. That is exactly how a `TimelineBlock` behaves, and `claimClick` —
 * reached here through the `onOpen` the axis wires — is what keeps the click a
 * finished drag leaves behind from opening the editor over the change it just
 * made.
 */
function CoverageCapsule({
  band,
  laneCount,
  drawn,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  onOpen,
  onNudge,
  panel,
  selected = false,
}: {
  band: TimelineCoverageBand<TrackCoverage>;
  /** How many columns the track is divided into — the widest group's count. */
  laneCount: number;
  /** Where the drag has it, which is not where `layout` put it. */
  drawn: { top: number; height: number; readout: TimelineReadout } | null;
  onPointerDown?: (
    annotation: TrackCoverage,
    kind: DragKind,
    event: ReactPointerEvent<HTMLElement>,
  ) => void;
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: () => void;
  /** Open this window's panel. Absent where no retag is wired. */
  onOpen?: (annotation: TrackCoverage) => void;
  /** Everything the panel commits, handed through to it. */
  panel: {
    categories: readonly string[];
    onPickTag: (tags: CoverageTags) => void;
    onStart?: (clockTime: string) => void;
    onEnd?: (clockTime: string) => void;
    onRemove?: () => void;
    onCancel: () => void;
  };
  onNudge?: (annotation: TrackCoverage, steps: number, fine: boolean) => void;
  /** Whether this is the window the tag editor is open on. */
  selected?: boolean;
}) {
  /**
   * Where in the capsule the panel hangs from.
   *
   * Starts at the leading corner, which is where a keyboard opening anchors
   * and where every opening used to. `TimelineBlock` keeps the same pair for
   * the same reason: a panel that opens at the pointer is a panel the planner
   * does not have to go and find.
   */
  const [anchor, setAnchor] = useState({ x: 0, y: 0 });
  const { annotation, lane } = band;
  const top = drawn ? drawn.top : band.top;
  const height = drawn ? drawn.height : band.height;
  const name = annotation.category ?? annotation.label;
  const live = onPointerDown !== undefined;
  /**
   * Whether the panel has a field open.
   *
   * Escape belongs to a half-typed field before it belongs to the panel around
   * it, and Radix listens for it on the document in the capture phase — so
   * without this the panel closes before the field sees the key and the planner
   * loses the edit instead of reverting it. The block's panel keeps the same
   * guard.
   */
  const [fieldOpen, setFieldOpen] = useState(false);
  // Below three edges' worth there is no body left to grab — see `EDGE_PX`.
  const edges = live && height >= 3 * EDGE_PX;

  return (
    <Popover
      open={selected}
      onOpenChange={(next) => {
        if (next) return;
        setFieldOpen(false);
        panel.onCancel();
      }}
    >
      <div
      data-testid={`coverage-capsule-${annotation.id}`}
      data-coverage-lane={lane}
      /* One element, not a positioned wrapper around a painted box: this is the
         capsule, the drag handle, the click target and the focusable thing at
         once, and a wrapper would be what a pointer and a test landed on
         instead — an event on a parent never reaches its child. `top` and
         `height` come from `layout()` untouched, like every vertical number on
         this axis; the horizontal pair is this file's own division of a fixed
         column (see `laneBox`).

         Named, focusable and clickable, because the drag is otherwise
         mouse-only. A window is the one thing on this panel that leaves the app
         — `{{coverage_hours}}` merges off these rows — so a planner who cannot
         use a pointer has to be able to move one, and the arrow keys are the
         same twin of the drag the blocks already offer.

         The accessible name is the capsule's own text, through the content
         rather than an `aria-label`: it is the one control on a window now, so
         there is no second thing answering to the same words, and a name that
         is what the planner sees is findable by a test, by a screen reader and
         by eye alike. The old rail had to name its capsule `Retime …` to tell
         it apart from the floating label beside it; with the label gone, so is
         the reason.

         `title` carries the tag and the hours whole, which is what makes the
         truncation below safe — see the text block. It is last in the
         accessible-name order, so content still wins the name.

         `overflow-hidden` so the text is clipped by the window's real length
         rather than growing it: a thirty-minute booking is 60px, and a box that
         stretched to fit two lines would be a height that lies about the hours
         it stands for. `rounded-lg` is the block's rounding one step down for a
         narrower box, and not a pill — a pill is the chip idiom this design
         deleted. */
      {...(live
        ? {
            role: 'button' as const,
            tabIndex: 0,
            'aria-keyshortcuts': 'ArrowUp ArrowDown',
            title: `${name} · ${annotation.hours}`,
          }
        : { 'aria-hidden': true as const })}
      /* Three fills for three states, all one hue.
      
         At rest the capsule is `--color-coverage`: white text on it is 4.94:1
         and it is 3.9:1 against the track's ground.
      
         Selected — the window whose editor is open — darkens to
         `--color-coverage-strong`. White text rises to 8.63:1 there, and the
         two fills are 1.75:1 against *each other*, which is what makes the
         chosen one findable in a column of its neighbours without a second hue
         or a badge. The editor card is open beside it, but a planner who has
         scrolled should still be able to see which window it belongs to.
      
         Hovered, the capsule takes a ring in the same dark clay: 6.82:1 against
         the ground, so it reads immediately. A ring rather than a fill change
         because the fill already means something here — hover is "pressing this
         selects it", which is a different claim from "this is selected", and
         the two have to be able to show at once. */
      className={`pointer-events-auto absolute overflow-hidden rounded-lg text-left transition-colors focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-primary/70 ${
        selected ? 'bg-[var(--color-coverage-strong)]' : 'bg-[var(--color-coverage)]'
      } ${
        live
          ? 'cursor-grab ring-[var(--color-coverage-strong)] hover:ring-2 active:cursor-grabbing'
          : ''
      }`}
      style={{ top, height, ...laneBox(lane, laneCount) }}
      onPointerDown={
        onPointerDown
          ? (e) => {
              // Stopped here, and it is not tidiness. The track is this
              // element's ancestor and the track's empty ground is the sweep
              // target, so a press that bubbled would arm *both* gestures at
              // once: the capsule would follow the pointer while a provisional
              // span was drawn under it, and the release would file a retime
              // and open a tag draft for a window nobody asked for.
              e.stopPropagation();
              onPointerDown(annotation, 'body', e);
            }
          : undefined
      }
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onClick={
        onOpen
          ? (e) => {
              // Where the panel should hang, which is where the planner
              // pointed — the block's own arrangement, measured the same way.
              // Anchored to the capsule instead, the panel opened against the
              // edge of an eleven-rem column and so always off to one side of
              // it, nowhere near the hand that asked for it.
              const box = e.currentTarget.getBoundingClientRect();
              setAnchor({ x: e.clientX - box.left, y: e.clientY - box.top });
              onOpen(annotation);
            }
          : undefined
      }
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          // A `div` with `role="button"` gets no keyboard activation for free,
          // and the editor is the only way to retag or remove a window: without
          // this the keyboard could move a window but never change who it
          // covers.
          e.preventDefault();
          // A keyboard opening has no pointer to hang on, so it anchors at the
          // capsule's leading corner — which is where every opening used to be
          // and is where the block's does from the keyboard too.
          setAnchor({ x: 0, y: 0 });
          onOpen?.(annotation);
          return;
        }
        if (!onNudge || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
        e.preventDefault();
        onNudge(annotation, e.key === 'ArrowDown' ? 1 : -1, e.altKey);
      }}
    >
      {/* The tag and the hours, on two lines inside the capsule — which is the
          whole of what the extra width bought and the whole of what the rail
          could not do.

          `truncate` on each line with the full string in the capsule's `title`,
          which is `TimelineBlock`'s own answer to a cramped box rather than a
          second idiom: two overlapping windows halve the track and
          `Photographer` does not fit the 77px that leaves, so it ellipsises and
          the tooltip has it whole. Stacked rather than one line for the same
          reason — a name and a nine-hour range on one line would ellipsise the
          name away at any lane count.

          `tabular-nums` on the hours, matching the gutter and the drag readout:
          times set proportionally shift sideways between two windows whose
          hours differ only in a digit, and a stack of capsules is exactly where
          a planner compares them column-wise.

          The server's own string, printed and not formatted. `hours` is what
          `resolveCoverage` built from the wedding's start time and what
          `{{coverage_hours}}` merges into a vendor's email; the two disagreeing
          by a minute is a vendor turning up at the wrong time.

          A short window simply shows less of this. The box is floored at
          `MIN_BLOCK_PX` by the geometry, so a five-minute window is twelve
          pixels tall and clips to part of its first line — and still carries the
          whole of both in its `title`. */}
      {/* The live times, while there are live times.

          The two text rows below go on printing `annotation.hours` throughout
          a drag, because that string is the server's own and is what a drop
          will commit — but during the gesture it is the one number on screen
          that has not moved, and it is the number the planner is watching. The
          plate is what answers them, and it is the same plate a block and a
          draft put up, in the same place, for the same reason. */}
      {drawn && <ReadoutPlate readout={drawn.readout} />}

      <div className="px-1.5 py-1 leading-tight text-white">
        {/* The comma is inside this line, flush against the name, and that
            position is the whole trick. The line break between the two divs is
            CSS and CSS is not in the accessible name, but the block boundary
            does contribute a space — so a separator in its own element between
            them reads as "Photographer , 11:00 AM", with the comma floating.
            Inside the name, the boundary's own space falls after it and the
            capsule answers to "Photographer, 11:00 AM – 8:00 PM", which is what
            the eye already infers from the break and what a screen reader
            should say. */}
        <div className="truncate text-[0.6875rem] font-medium">
          {name}
          <span className="sr-only">,</span>
        </div>
        <div className="truncate text-[0.6875rem] tabular-nums">{annotation.hours}</div>
      </div>

      {/* The press is stopped from bubbling inside `EdgeHandles`, which this
          capsule is the reason for: its own body handler is the parent and
          would read a press here as a move, and the track above that would
          read it as the start of a sweep. */}
      {edges && (
        <EdgeHandles
          drawn={height}
          onGrab={(edge, event) => onPointerDown?.(annotation, edge, event)}
          handlers={{ onPointerMove, onPointerUp, onPointerCancel }}
        />
      )}

      {/* Where the panel hangs from: a zero-size point at the pixel the
          planner pressed. Mounted up front rather than with the panel, which
          is `TimelineBlock`'s own reasoning — a surface that is replaced the
          first time a panel opens is a surface whose focus goes with it. */}
      <PopoverAnchor asChild>
        <div
          data-coverage-anchor
          aria-hidden="true"
          className="pointer-events-none absolute size-0"
          style={{ left: anchor.x, top: anchor.y }}
        />
      </PopoverAnchor>
      </div>

      <PopoverContent
        data-coverage-editor
        side="right"
        align="start"
        sideOffset={10}
        collisionPadding={12}
        /* Re-measured every frame while it is up, because a capsule can be
           dragged out from under its own panel exactly as a block can. */
        updatePositionStrategy="always"
        aria-label={`${annotation.category ?? annotation.label} details`}
        /**
         * A press in the panel is not a press on the capsule it belongs to.
         *
         * The panel is portalled into `document.body`, and that is not enough:
         * React propagates a synthetic event through the tree an element was
         * *rendered* in rather than the DOM it was portalled into, so a press
         * on a field in here arrives at the capsule's own handler — which arms
         * a drag of the window, and at the capsule's `onClick`, which re-opens
         * the panel and moves it to wherever the field happened to be. The
         * same thing the draft's control has to do, one level out.
         */
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        onEscapeKeyDown={(event) => {
          if (fieldOpen) event.preventDefault();
        }}
        className="w-60 rounded-2xl border-[var(--color-outline-variant)] p-0 shadow-[0_12px_36px_rgba(49,51,47,0.16)] motion-reduce:animate-none!"
      >
        <CapsulePanel
          annotation={annotation}
          categories={panel.categories}
          onFieldOpen={setFieldOpen}
          onPickTag={panel.onPickTag}
          onStart={panel.onStart}
          onEnd={panel.onEnd}
          onRemove={panel.onRemove}
          onCancel={panel.onCancel}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * The span the planner is drawing, or has drawn and not yet named.
 *
 * Lighter than a placed capsule and ringed rather than solid: clay at 80%
 * measures **2.83:1** against the track's ground, visibly short of the 3.90:1
 * of a window that exists. The difference is the statement — this is not a
 * window yet. Nothing about it is interactive; the gesture that made it is
 * still running, or the tag control over it has the focus.
 *
 * The middle of three states, and on the track's ground the scale reads
 * **1.72:1** for a window a press would make, **2.83:1** for one being drawn,
 * **3.90:1** for one that is stored. Three visible steps for three degrees of
 * commitment, which is what keeps them from being read as three decorations.
 *
 * Those alphas were chosen against the white card, where the same three measure
 * 1.85, 3.34 and 4.94. The track's ground is a step darker, so every figure
 * comes down with it and the swept span's 2.83 now sits just under the 3:1 a
 * *standing* graphic would need. That is the one figure in this file worth a
 * second look. It is defensible — the shape exists only while the pointer is
 * held, the ring at full strength is what actually bounds it, and the planner's
 * own hand is the thing saying where it is — but it is a real change from the
 * rail rather than a number that happened to survive the move.
 */
function ProvisionalCapsule({
  top,
  height,
  testId,
}: {
  top: number;
  height: number;
  testId: string;
}) {
  return (
    <div
      data-testid={testId}
      aria-hidden="true"
      className={`pointer-events-none absolute inset-x-0 ${PROVISIONAL}`}
      style={{ top, height }}
    />
  );
}

/** The provisional surface, so the inert shape and the live one cannot drift. */
const PROVISIONAL = 'rounded-lg bg-[var(--color-coverage)]/80 ring-1 ring-[var(--color-coverage)]';

/** The naming field's own height — `h-7`, the timeline draft's field. */
const DRAFT_INPUT_PX = 28;

/** One category row: `py-1` either side of a `text-xs` line. */
const DRAFT_ROW_PX = 24;

/** How many of those are shown before the list scrolls. */
const DRAFT_ROWS = 3;

/** The list's margin above it and its own padding either side of the rows. */
const DRAFT_LIST_CHROME_PX = 8;

/**
 * The strip of card left clear beneath the field, so the draft can be moved.
 *
 * The card *is* the body target — `inset-0`, under everything else in it — and
 * the field over it is live across the card's whole width. So a floor of two
 * strips and a field was a floor with no body left: a draft swept short enough
 * to sit on it could be re-lengthened by either edge and typed into, and could
 * not be dragged anywhere, because there was no pixel of it a pointer could
 * reach that did not already belong to a resize strip or to the field. A long
 * sweep hid that completely — a card taller than its own contents has the body
 * showing below them — so the gesture worked on a photographer's nine hours
 * and not on a DJ's short span, which is how it was reported.
 *
 * Two strips' worth rather than one. A strip is the target size this axis
 * deals in, and the body is reached by aiming *between* two of them; eight
 * pixels of it directly under the field and directly over the bottom edge is
 * the same crowding `EdgeHandles` refuses when it gives a short card no edges
 * at all.
 */
const DRAFT_BODY_PX = EDGE_PX * 2;

/**
 * The shortest a draft's card is drawn while it is only a field.
 *
 * Two eight-pixel strips, the field between them, and the body below it. A
 * fresh draft shows its span and a place to name it and nothing else — which
 * is what the timeline's draft shows, and the whole reason the rows below it
 * are not out to begin with: a list unfurled on arrival fills the card and
 * hides the very thing the planner has just drawn.
 */
const DRAFT_FIELD_PX = EDGE_PX * 2 + DRAFT_INPUT_PX + DRAFT_BODY_PX;

/** The list's own room: three rows, and the rest scrolls. */
const DRAFT_LIST_PX = DRAFT_ROW_PX * DRAFT_ROWS;

/**
 * And the shortest the card is drawn once those rows are showing.
 *
 * **Added up rather than chosen**, which is the whole point of the five
 * constants above it. It was a round 120 — the hour a click on the track makes
 * — against contents that actually came to 127, so the last row and the resize
 * grip under it were pushed out through the bottom of the card and onto the
 * track. A floor picked for how it reads and a floor that holds the contents
 * are two different numbers, and only one of them can be hard-coded.
 *
 * It lands within a few pixels of that hour anyway, so the commonest draft
 * there is is still drawn at very near its true length. What matters is that
 * it cannot drift from what is in it: change the field, the rows or how many
 * show, and this follows.
 */
const DRAFT_CARD_PX = DRAFT_FIELD_PX + DRAFT_LIST_CHROME_PX + DRAFT_LIST_PX;

/**
 * The same span, once the pointer has let go of it — and the field that names
 * it, inside it.
 *
 * One card, not a card beside a shape. The draft used to be a translucent span
 * in the column with a separate tag card floating next to it, which meant the
 * thing the planner was looking at and the thing they were typing into were two
 * objects that happened to be at the same height. `NewItemDraft` has never been
 * two objects: the block *is* the field's card, drawn at the minutes it claims,
 * and a planner reads one thing and types into it.
 *
 * So this is the block's own arrangement in the track's column. The card is the
 * span; the field sits in it inset clear of the top strip, exactly where the
 * timeline draft's label sits; the rows the field offers are under it in the
 * same card; and the card's bottom edge is the resize handle, with the grip
 * that says so.
 *
 * **It is drawn taller than its minutes when its contents need the room**, and
 * that is the one place it departs from the span it describes. A field, a few
 * rows and a way out come to more than the hour a click makes. The block makes
 * the same trade at `MIN_CARD_PX` and states it the same way: the drawn box
 * rounds up while `top` stays exact, because a card that cannot hold its own
 * contents is not a card. The edges are measured against the drawn box — see
 * `EdgeHandles` — so the handle is always on the border the planner is aiming
 * at, and the readout above says what the span actually is while it is dragged.
 */
function DraftCapsule({
  top,
  height,
  readout,
  grab,
  categories,
  onPlace,
  onCancel,
}: {
  top: number;
  height: number;
  readout: TimelineReadout | null;
  grab?: {
    onPointerDown: (kind: DragKind, event: ReactPointerEvent<HTMLElement>) => void;
    onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerCancel: () => void;
  };
  categories: readonly string[];
  /** Absent where no placement is wired, which is how the drag suites mount it. */
  onPlace?: (tags: CoverageTags) => void;
  onCancel: () => void;
}) {
  /**
   * A press on a grab target must not move focus.
   *
   * The field in this card is the one that names the window, and the control
   * around it commits when it is left — so a resize that blurred it would place
   * the window it was in the middle of sizing. The timeline draft carries the
   * same line for the same reason.
   */
  const keepFocus = (event: ReactMouseEvent<HTMLElement>) => event.preventDefault();
  const pointers = grab && {
    onPointerMove: grab.onPointerMove,
    onPointerUp: grab.onPointerUp,
    onPointerCancel: grab.onPointerCancel,
    onMouseDown: keepFocus,
  };
  /**
   * Whether the field has its rows out, which is the only thing that changes
   * how much room this card needs.
   *
   * Held here rather than in the control because it is the *card* that has to
   * answer for its own height, and the control is the only thing that knows.
   */
  const [listOpen, setListOpen] = useState(false);
  /* Below the floor the drawn box rounds up; above it the card is the span. */
  const drawn = onPlace
    ? Math.max(height, listOpen ? DRAFT_CARD_PX : DRAFT_FIELD_PX)
    : height;

  return (
    <div
      data-testid="coverage-draft-capsule"
      {...(onPlace ? { 'data-coverage-draft': '' } : {})}
      /* The timeline draft's surface, not the capsule's clay: this is floating
         over a column it has not joined yet, and a placed window's fill would
         say it had. */
      className={cn(
        /* Clipped, and that is not belt-and-braces: the card is a *span*, so
           its height is a fact rather than a consequence of what is in it.
           Anything that cannot fit scrolls inside, and nothing is allowed to
           spill onto the track and read as a row of a window it is not in. */
        'absolute inset-x-0 z-20 overflow-hidden rounded-lg',
        onPlace
          ? 'bg-card shadow-[0_8px_28px_rgba(49,51,47,0.16)] ring-1 ring-inset ring-primary/75'
          : PROVISIONAL,
        grab ? 'pointer-events-auto' : 'pointer-events-none',
      )}
      style={{ top, height: drawn }}
    >
      {readout && <ReadoutPlate readout={readout} />}

      {grab && pointers && (
        /* The body, which moves the whole span. Behind the field rather than
           over it, so a press on the card moves the draft and a press in the
           field places a caret — the stacking the timeline draft uses. Its
           press is stopped because the track reads a press on its ground as
           the start of a new sweep, which would replace the draft. */
        <div
          data-timeline-grab="body"
          className="absolute inset-0 cursor-grab touch-none active:cursor-grabbing"
          onPointerDown={(event) => {
            event.stopPropagation();
            grab.onPointerDown('body', event);
          }}
          {...pointers}
        />
      )}

      {onPlace && (
        /* Inset by a strip's worth top and bottom, so neither resize target is
           ever laid over the field — the timeline draft's rule, and the same
           eight pixels. */
        <div
          className="pointer-events-none absolute inset-x-0 top-0 px-1.5"
          style={{ paddingTop: EDGE_PX, paddingBottom: EDGE_PX }}
          /* In the track's own subtree now rather than portalled out of it, so
             a press in here would otherwise reach the ground and start a sweep. */
          onPointerDown={(event) => event.stopPropagation()}
        >
          <TagControl
            className="pointer-events-auto"
            categories={categories}
            onListOpenChange={setListOpen}
            onPick={onPlace}
            onCancel={onCancel}
          />
        </div>
      )}

      {grab && pointers && (
        <EdgeHandles drawn={drawn} grip onGrab={grab.onPointerDown} handlers={pointers} />
      )}
    </div>
  );
}


/**
 * A window's four facts, each one click from being the field that produced it.
 *
 * The grammar is the block's, deliberately and to the letter: a fact is a
 * button, pressing it makes it an input, blur or Enter commits that one field,
 * Escape puts it back, and there is no Save. A planner editing the day and a
 * planner editing who is booked for it should not have to learn two ways to
 * change a time, and until this existed they did — a window's times were
 * draggable and nothing else, so the one thing a planner could not do to a
 * vendor's hours was *type* them.
 *
 * `Starts` and `Ends` rather than the block panel's start-and-length, and that
 * is not a drift from parity but a read of what each thing is. A timeline item
 * is an event with a duration — "the ceremony runs half an hour" — and may have
 * no stored length at all. A window is a booking, which both parties state as a
 * range: the capsule already prints `11:00 AM – 8:00 PM`, the vendor's email
 * merges the same string, and a panel asking for a length instead would be the
 * one surface in the chain speaking a different way about it.
 */
function CapsulePanel({
  annotation,
  categories,
  onFieldOpen,
  onPickTag,
  onStart,
  onEnd,
  onRemove,
  onCancel,
}: {
  annotation: TrackCoverage;
  categories: readonly string[];
  /** So the popover around this can give Escape to an open field first. */
  onFieldOpen: (open: boolean) => void;
  onPickTag: (tags: CoverageTags) => void;
  /** Absent where no retime is wired, which is how the drag suites mount it. */
  onStart?: (clockTime: string) => void;
  onEnd?: (clockTime: string) => void;
  onRemove?: () => void;
  onCancel: () => void;
}) {
  /** Which fact is currently a field. One at a time: this is not a form. */
  const [field, setField] = useState<'tag' | 'start' | 'end' | null>(null);
  const open = (next: 'tag' | 'start' | 'end') => {
    setField(next);
    onFieldOpen(true);
  };
  const close = () => {
    setField(null);
    onFieldOpen(false);
  };

  const name = annotation.category ?? annotation.label;
  const start = formatTimeOfDay(annotation.clockTime) ?? annotation.clockTime;
  const end = formatTimeOfDay(annotation.endClockTime) ?? annotation.endClockTime;
  /* Which day the end lands on, when it is not the one the window began. A
     bare `1:00 AM` under a start of `11:00 AM` reads as ten hours earlier
     rather than fourteen later, and this is the only thing on the row saying
     otherwise. */
  const endDay = dayText(annotation.endDayOffset);

  /** One committed field is one commit, and it closes the field it came from. */
  const commit = (send: () => void) => {
    close();
    send();
  };

  return (
    <div>
      {/* No card, no ring and no shadow of its own: the popover is the card,
          the way the block's panel has it. Two nested surfaces for one window
          read as a plate stuck to a panel. */}
      <div className="space-y-1.5 px-3.5 pb-2.5 pt-3">
        <DetailRow label="Covers" open={field === 'tag'}>
          {field === 'tag' ? (
            <TagControl
              categories={categories}
              initial={annotation}
              onPick={(tags) => commit(() => onPickTag(tags))}
              onCancel={close}
            />
          ) : (
            <FieldButton
              name={`Change who ${name} covers`}
              className={DETAIL_FIELD}
              onOpen={() => open('tag')}
            >
              {name}
            </FieldButton>
          )}
        </DetailRow>

        <DetailRow label="Starts" open={field === 'start'}>
          {field === 'start' && onStart ? (
            <TimeField
              id={`coverage-${annotation.id}-start`}
              className={PANEL_TIME}
              value={annotation.clockTime}
              onCommit={(clock) => commit(() => onStart(clock))}
              onCancel={close}
            />
          ) : (
            <FieldButton
              name={`Change when ${name} starts`}
              className={`${DETAIL_FIELD} tabular-nums`}
              onOpen={() => onStart && open('start')}
            >
              {start}
            </FieldButton>
          )}
        </DetailRow>

        <DetailRow label="Ends" open={field === 'end'}>
          {field === 'end' && onEnd ? (
            <TimeField
              id={`coverage-${annotation.id}-end`}
              className={PANEL_TIME}
              value={annotation.endClockTime}
              onCommit={(clock) => commit(() => onEnd(clock))}
              onCancel={close}
            />
          ) : (
            <FieldButton
              name={`Change when ${name} finishes`}
              className={`${DETAIL_FIELD} tabular-nums`}
              onOpen={() => onEnd && open('end')}
            >
              {end}
              {endDay && (
                <span className="label-caps ml-1.5 text-[0.625rem] text-foreground/75">
                  {endDay}
                </span>
              )}
            </FieldButton>
          )}
        </DetailRow>
      </div>

      {/* Named, like the block panel's. This is where a planner reads a window
          rather than points at it, and a removal that says which window it is
          about is the difference between a considered one and an undo. */}
      <div className="flex items-center justify-end gap-1 border-t border-[var(--color-outline-variant)] p-1.5">
        <Button variant="ghost" size="xs" data-coverage-cancel onClick={onCancel}>
          Cancel
        </Button>
        {onRemove && (
          <Button
            variant="ghost"
            size="xs"
            data-coverage-remove
            className="text-destructive hover:bg-destructive/10"
            onClick={onRemove}
          >
            Remove {name}
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * The one question a swept span cannot answer: who it covers.
 *
 * The sibling of `NewItemDraft` in `TimelineAxis.tsx`. A sweep has said *when*,
 * exactly as a click on empty day has, and the one thing neither can infer is
 * *what* — so this asks that and nothing else. No start, no length, no Save:
 * the times were dragged and they are dragged again to change them.
 *
 * Which is the whole of why the form it replaces is gone. `CoverageForm` asked
 * three questions through a `TimePicker`, an hours/minutes pair and a picker,
 * behind an "Add coverage" button in the panel's header — a second grammar for
 * placing something on a timeline where every other thing is placed by pointing
 * at it. One control is left, and it is the only one a drag cannot supply.
 *
 * A category *or* free text, never both, because the schema says so: exactly
 * one of `category` and `label` is set on a stored row, and
 * `coverageHasExactlyOneTag` refuses anything else. One field answers both:
 * typing filters the planner's categories and simultaneously offers what they
 * typed as a new tag, so the two outcomes share a gesture instead of being
 * two modes with a switch between them.
 *
 * **Stacked, and the width of the track.** It used to be a `w-56` row floating
 * over the lane area, because the rail it belonged to was six pixels wide. The
 * track is eleven rem, so the editor lives in it — but eleven rem minus the
 * inner gutter is 160px, and a picker and a Remove side by side in that leave
 * the picker 110px, narrow enough to ellipsise the very tag it is showing the
 * planner. A column fits both at full width and costs one row of height in a
 * place that has height to spare.
 */
/**
 * A row in the tag list: one of the planner's categories, or the words they
 * have typed offered as a new tag.
 *
 * The two are the schema's two tag kinds — `category` and `label` — and keeping
 * them as distinct row kinds rather than bare strings is what makes
 * "Photographer" the category and a typed "Photographer Jr" the label without
 * anything having to guess which it meant.
 */
type TagRow = { kind: 'category'; name: string } | { kind: 'create'; text: string };

/**
 * A row's identity, stable across a re-filter.
 *
 * The active row is held by this rather than by index, because the list
 * narrows as the planner types: an index would quietly come to mean a
 * different row, and `aria-activedescendant` would name one the eye is not on.
 * Prefixed, so a category called "Second shooter" and a typed "Second shooter"
 * cannot collide — they are the schema's two different tag kinds.
 */
function rowKey(row: TagRow): string {
  return row.kind === 'category' ? `c:${row.name}` : 'create';
}

/**
 * Makes a new `TagRow` kind a compile error everywhere one has to be handled,
 * rather than a row that renders fine and does nothing when pressed — the same
 * guard `VendorRecipientField` uses on its own rows and for the same reason.
 */
function assertNeverTagRow(row: never): never {
  throw new Error(`Unhandled tag row: ${JSON.stringify(row)}`);
}

function TagControl({
  className,
  categories,
  initial,
  onListOpenChange,
  onPick,
  onCancel,
}: {
  /** The surface is the caller's: this brings a field and its rows. */
  className?: string;
  categories: readonly string[];
  /**
   * Told when the rows come out, because the surface around this may have to
   * make room for them — the draft capsule is a span first and a card second,
   * and how tall it is drawn is its own business to answer for.
   */
  onListOpenChange?: (open: boolean) => void;
  initial?: TrackCoverage;
  onPick: (tags: CoverageTags) => void;
  onCancel: () => void;
}) {
  /**
   * What the planner has typed, which is both the filter and the new tag.
   *
   * Seeded from whatever the window already carries — its category or its own
   * words — so opening the editor shows the tag rather than an empty field
   * waiting to replace it.
   */
  const [query, setQuery] = useState(initial?.category ?? initial?.label ?? '');
  /** Which row the keyboard is on, by identity rather than index: the list
   *  re-filters as the planner types and an index would follow the wrong row. */
  const [activeKey, setActiveKey] = useState<string | null>(null);
  /**
   * Whether the rows are showing.
   *
   * Closed on arrival, which is the difference between this reading as a field
   * on a span and as a menu that happens to be in one. A list unfurled the
   * instant a draft appears fills the card and hides the span the planner has
   * just drawn — and the span is the thing they are deciding about. Typing
   * opens it, because typing is a search; so does ArrowDown, which is what a
   * planner who would rather look than type presses.
   */
  const [listOpen, setListOpen] = useState(false);
  const spent = useRef(false);
  /** Unique per control, so two of these on screen cannot share option ids. */
  const inputId = useId();

  const openList = (open: boolean) => {
    setListOpen(open);
    onListOpenChange?.(open);
  };

  const leave = (tags: CoverageTags | null) => {
    if (spent.current) return;
    spent.current = true;
    if (tags) onPick(tags);
    else onCancel();
  };

  const typed = query.trim();

  /**
   * The planner's categories, with their own words offered as a row when what
   * they typed is not one of them.
   *
   * A row in the *same* array as the categories rather than a control beside
   * the list, which is the lesson `VendorRecipientField` records: anything
   * outside the list is invisible to keyboard navigation and to the filter, and
   * so is an affordance that only works with a mouse.
   *
   * This replaced a `Select` with a reserved sentinel value meaning "something
   * else", which swapped the whole control for a text box. Two controls, two
   * modes, and a tag that could only be typed *after* deciding to type it. One
   * field that filters as you type and offers what you typed is the same two
   * outcomes without the mode: a photographer is three keystrokes and a
   * "Second shooter" is the row underneath them.
   */
  const stored = initial?.category;
  const known = stored && !categories.includes(stored) ? [stored, ...categories] : [...categories];
  const rows: TagRow[] = [
    ...known.map((name) => ({ kind: 'category' as const, name })),
    // Only when it is actually a new name. Offering "use Photographer" beneath
    // the Photographer row is two rows doing one thing, and the planner has to
    // work out which.
    ...(typed && !known.some((name) => name.toLowerCase() === typed.toLowerCase())
      ? [{ kind: 'create' as const, text: typed }]
      : []),
  ].filter(
    // Filtered here rather than by the control, now that the control is this
    // file's own. Same predicate the combobox was given: the create row is
    // never filtered out, because it *is* the query and hiding it when the
    // query does not match itself would remove the one row that always applies.
    (row) => row.kind === 'create' || row.name.toLowerCase().includes(typed.toLowerCase()),
  );

  /** A row's identity, which is what the active-row state holds. */
  const active = activeKey && rows.some((row) => rowKey(row) === activeKey) ? activeKey : null;

  /**
   * What Enter takes when nothing is highlighted.
   *
   * The create row if there is one — a planner who typed a name and pressed
   * Enter meant that name — and otherwise the only row left when the query has
   * narrowed the list to one. With several still matching, Enter does nothing
   * rather than guessing which.
   */
  const fallbackRow =
    rows.find((row) => row.kind === 'create') ?? (rows.length === 1 ? rows[0] : undefined);

  const step = (delta: number) => {
    if (rows.length === 0) return;
    const index = active ? rows.findIndex((row) => rowKey(row) === active) : -1;
    const next = index === -1 ? (delta > 0 ? 0 : rows.length - 1) : index + delta;
    const bounded = Math.min(rows.length - 1, Math.max(0, next));
    setActiveKey(rowKey(rows[bounded]!));
  };

  const pick = (row: TagRow) => {
    switch (row.kind) {
      case 'category':
        leave({ category: row.name, label: null });
        return;
      case 'create':
        leave({ category: null, label: row.text });
        return;
      default:
        return assertNeverTagRow(row);
    }
  };

  return (
    <div
      /* No card of its own any more. It used to bring one, because it floated
         over the track as a thing in its own right — and now it never does:
         inside the draft capsule the card *is* the span, and inside the
         capsule's panel the card is the popover. Two nested surfaces for one
         window read as a plate stuck to a panel. */
      className={cn('flex w-full flex-col gap-1', className)}
      /**
       * Leaving the control settles it, which it could not do before.
       *
       * The old note here said nothing cancels on focus, and it was right at
       * the time: both pickers portalled their list and moved focus into it,
       * and the blur that fired arrived before any open-state callback could
       * say where focus had gone — a guard could not win that race, and while
       * one was in place the control abandoned itself the instant the planner
       * reached for the list.
       *
       * The list is in the box now and its rows take the press without taking
       * focus, so there is no race left to lose: anything that moves focus out
       * of this element is the planner moving on. Which is what a draft needs,
       * because one that survives being clicked away is one the planner has to
       * go back and dismiss.
       *
       * It does what Enter does — the highlighted row, or what Enter would
       * fall back to — and abandons the draft when Enter would have done
       * nothing. A span nobody tagged is a press the planner thought better
       * of, which is the timeline draft's rule for a label nobody typed.
       */
      onBlur={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        const row = rows.find((candidate) => rowKey(candidate) === active) ?? fallbackRow;
        if (row) pick(row);
        else leave(null);
      }}
      onKeyDown={(e) => {
        // `defaultPrevented` is how the field says it handled the key itself.
        // It no longer ever does for Escape: the list is in the box rather than
        // in a popup, so there is no menu for Escape to close first and Escape
        // means what it means everywhere else on this axis — put it back. The
        // guard stays because the field is still free to claim the key, and a
        // control that assumed it never would is one bug away from eating an
        // edit. `TimelineItemForm` reads Escape the same way.
        if (e.key !== 'Escape' || e.defaultPrevented) return;
        e.stopPropagation();
        leave(null);
      }}
    >
      {/* The field, and its list in the box with it.

          Not the app's `Combobox`, and not because that control is wrong —
          because its list is a floating layer and this one should not be. Base
          UI positions a combobox popup with `position: absolute` or `fixed` and
          offers nothing in between, and its `List` and `Popup` both require the
          positioner's context, so an in-flow list is not a setting on that
          component; it is a different component.

          Which is the one this panel's other two fields already use. `TimePicker`
          hand-rolls exactly this — an input that owns `aria-activedescendant`,
          a `role="listbox"` of `role="option"` rows, arrow keys that walk them
          and Enter that takes one — and `Starts` and `Ends` are two of them.
          Leaving the tag on a portalled popup made this the one field of three
          whose menu flew out over the day, nested inside a panel that is itself
          a popover. Now the panel simply grows, and nothing can clip it or need
          flipping.

          The conventions below are `TimePicker`'s on purpose, down to the
          `onMouseDown` that keeps the caret from leaving on the way to a row. */}
      <div>
        <Input
          id={`${inputId}`}
          /* `h-7` and `text-xs`, which is the timeline draft's own field. The
             two name the same kind of thing in the same kind of card, and the
             roomier default sized this one out of the span it sits in. */
          className="h-7 rounded-lg px-2 text-xs"
          role="combobox"
          aria-label="Who it covers"
          aria-expanded={listOpen}
          aria-controls={`${inputId}-listbox`}
          aria-activedescendant={active ? `${inputId}-option-${active}` : undefined}
          aria-autocomplete="list"
          autoComplete="off"
          autoFocus
          placeholder="Who it covers"
          maxLength={120}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            // Typing is a search, so typing is what brings the rows out.
            openList(true);
            // What was highlighted may not be in the list any more, and a
            // stale `aria-activedescendant` points at nothing.
            setActiveKey(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              // The way in for a planner who would rather look than type: the
              // first press shows what is on offer, the next walks it.
              if (!listOpen) return openList(true);
              step(1);
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              if (!listOpen) return openList(true);
              step(-1);
            } else if (e.key === 'Enter') {
              // The list owns Enter whenever it has a row highlighted. With
              // none, Enter takes what was typed, which is what a planner who
              // never touched the arrows means by it.
              e.preventDefault();
              e.stopPropagation();
              const row = rows.find((candidate) => rowKey(candidate) === active) ?? fallbackRow;
              if (row) pick(row);
            }
          }}
        />

        {/* Capped and scrolling rather than unbounded: a planner with twenty
            categories should not be handed a panel taller than the card the
            timeline sits in. */}
        {/* Three rows, then scroll. The cap is what keeps the control inside
            the hour a click makes — see `DRAFT_LIST_PX`. A planner with twenty
            categories types rather than scrolls, which is what the field is
            for and why the create row is always in the list. */}
        {listOpen && (
          <div
            id={`${inputId}-listbox`}
            role="listbox"
            /* Its own name, not the field's. A listbox that repeats the
               combobox's name is a second thing answering to it — which is how
               `TimePicker` reads, and is why a test reaching for that field has
               to disambiguate by role. Nothing here should have to. */
            aria-label="Categories"
            /* Set apart from the field above it rather than sharing its white:
               the card, the field and the rows were three things on one surface
               with no line between any of them, and a list that reads as more
               of the box it sits in is a list the eye parses rather than scans.
               A step down the surface ramp, inside a hairline of its own.

               Inside the card's padding, not bled to its edges. Bled, it
               painted over the `ring-inset` the card is outlined with — the
               ring is drawn *within* the box, so a child at `inset-x-0` covers
               it — and a list that erases the border down both sides of the
               card is a list that reads as hanging out of it. Which is what it
               looked like, and what was reported.

               The cap is a style rather than a class because it is the same
               number the card's floor is built from, and two spellings of one
               number is how the rows came to overflow the box to begin with.
               `mt-1` and `p-0.5` either side are `DRAFT_LIST_CHROME_PX`. */
            style={{ maxHeight: DRAFT_LIST_PX }}
            className="mt-1 overflow-y-auto rounded-md bg-[var(--color-surface-container-low)] p-0.5 ring-1 ring-inset ring-[var(--color-outline-variant)]"
          >
            {rows.length === 0 ? (
              <p className="px-1.5 py-1 text-xs text-muted-foreground">
                No categories yet — type a name
              </p>
            ) : (
              rows.map((row) => {
                const key = rowKey(row);
                return (
                  <div
                    key={key}
                    id={`${inputId}-option-${key}`}
                    role="option"
                    aria-selected={key === active}
                    /* Keeps the field focused through the click, so taking a row
                       never blurs the entry out from under itself. */
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(row)}
                    onMouseEnter={() => setActiveKey(key)}
                    className={cn(
                      'cursor-pointer truncate rounded-lg px-1.5 py-1 text-xs transition-colors duration-150',
                      /* A step further up the ramp than the list's own ground,
                         which it has to be now that the ground has moved: the
                         highlight was `-low` on white, and would be invisible
                         on `-low`. */
                      key === active && 'bg-[var(--color-surface-container-high)]',
                    )}
                  >
                    {row.kind === 'category' ? (
                      row.name
                    ) : (
                      /* The typed words quoted, so the row reads as the thing it
                         will make rather than as another category that happens
                         to be named oddly. */
                      <>Use “{row.text}”</>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* No drawn way out, which is the timeline draft's arrangement and now
          this one's: Escape puts it back and a name commits it. The Cancel and
          Remove this used to carry were right while it was a floating card in
          its own right — they are the *panel's* controls, and the panel still
          draws both. Inside a span there is no room for a button row that is
          not about the span, and a `Remove` on something that does not exist
          yet never had anything to remove.

          Nothing cancels on focus, which stays true and is the reason this is
          safe: the pickers move focus about and a draft left open posts
          nothing, with its span on screen saying what is waiting on a tag. */}
    </div>
  );
}
